import React, { useState, useEffect } from "react";
import { X, Loader2, Check, Copy, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { subscribeNewsletter } from "../lib/api";

const COPY = {
  nl: {
    kicker: "WELKOM BIJ LÉLUNE",
    title: "10% korting op je\neerste bestelling",
    text: "Schrijf je in voor onze nieuwsbrief en ontvang direct 10% korting, plus als eerste nieuws over launches.",
    placeholder: "Jouw e-mailadres",
    btn: "Claim mijn 10%",
    no: "Nee bedankt",
    successTitle: "Je code is klaar!",
    successText: "Gebruik deze code bij het afrekenen voor 10% korting:",
    copied: "Gekopieerd!",
    copy: "Kopieer code",
    shop: "Verder winkelen",
  },
  en: {
    kicker: "WELCOME TO LÉLUNE",
    title: "10% off your\nfirst order",
    text: "Sign up for our newsletter and get 10% off right away, plus be the first to hear about launches.",
    placeholder: "Your email address",
    btn: "Claim my 10%",
    no: "No thanks",
    successTitle: "Your code is ready!",
    successText: "Use this code at checkout for 10% off:",
    copied: "Copied!",
    copy: "Copy code",
    shop: "Continue shopping",
  },
};

const CODE = "LELUNE10";
const STORAGE_KEY = "lelune_promo_popup";

const NewsletterPopup = () => {
  const { lang } = useApp();
  const c = COPY[lang];
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setOpen(true), 4500);
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, "dismissed");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await subscribeNewsletter(email);
    } catch {
      /* still show code even if already subscribed / offline */
    } finally {
      setBusy(false);
      setDone(true);
      localStorage.setItem(STORAGE_KEY, "subscribed");
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[70] bg-[#2A201C]/50 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={close}
      />
      <div
        className={`fixed z-[71] left-1/2 top-1/2 w-[calc(100%-2.5rem)] max-w-[760px] -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
          open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
      >
        <div className="relative grid md:grid-cols-2 bg-[#FBF5F0] rounded-3xl overflow-hidden border border-[#EEE0D7] shadow-2xl">
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-white/70 flex items-center justify-center text-[#8A756C] hover:text-[#B4715C]"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Visual side */}
          <div className="hidden md:flex relative bg-gradient-to-br from-[#F3E0D6] via-[#EFD0C3] to-[#E4BBA9] items-center justify-center p-10">
            <div className="absolute inset-0 opacity-70" style={{ background: "radial-gradient(60% 55% at 50% 45%, rgba(212,150,128,0.55), transparent 70%)" }} />
            <div className="relative text-center">
              <span className="font-serif text-[110px] leading-none text-white/90 drop-shadow-sm">10%</span>
              <p className="mt-2 text-[12px] tracking-label uppercase text-[#7A5545]">korting · off</p>
            </div>
            <Sparkles className="twinkle absolute top-8 left-8 h-5 w-5 text-white/80" />
            <Sparkles className="twinkle absolute bottom-10 right-10 h-4 w-4 text-white/70" style={{ animationDelay: "1s" }} />
          </div>

          {/* Content side */}
          <div className="p-8 md:p-11">
            {!done ? (
              <>
                <span className="text-[11px] tracking-label uppercase text-[#B4715C]">{c.kicker}</span>
                <h2 className="mt-3 font-serif font-light text-4xl md:text-[42px] leading-[1.05] text-[#3A2C28] whitespace-pre-line">
                  {c.title}
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-[#6E5A52]">{c.text}</p>
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={c.placeholder}
                    className="w-full h-12 px-5 rounded-full border border-[#E0CDC2] bg-white text-sm focus:outline-none focus:border-[#B4715C]"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full h-12 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C] disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {c.btn}
                  </button>
                </form>
                <button onClick={close} className="mt-4 w-full text-center text-[11px] tracking-label uppercase text-[#A08678] hover:text-[#B4715C]">
                  {c.no}
                </button>
              </>
            ) : (
              <div className="flex flex-col h-full justify-center">
                <span className="h-12 w-12 rounded-full bg-[#E4F1E9] flex items-center justify-center text-[#3C7A57]">
                  <Check className="h-6 w-6" />
                </span>
                <h2 className="mt-5 font-serif text-4xl text-[#3A2C28]">{c.successTitle}</h2>
                <p className="mt-3 text-[15px] text-[#6E5A52]">{c.successText}</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="flex-1 h-14 rounded-2xl border-2 border-dashed border-[#D6B7A9] bg-white flex items-center justify-center">
                    <span className="font-serif text-2xl tracking-[0.2em] text-[#B4715C]">{CODE}</span>
                  </div>
                  <button
                    onClick={copyCode}
                    className="h-14 w-14 rounded-2xl bg-[#3A2C28] text-white flex items-center justify-center hover:bg-[#B4715C]"
                    aria-label="Copy code"
                  >
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                {copied && <p className="mt-2 text-[12px] text-[#3C7A57]">{c.copied}</p>}
                <button onClick={close} className="mt-6 h-12 rounded-full border border-[#D6B7A9] text-[#5A4740] text-[11px] tracking-label uppercase hover:border-[#B4715C] hover:text-[#B4715C]">
                  {c.shop}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsletterPopup;
